import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Globe, Target, CheckCircle2, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
export function WhyWebpages() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  const reasonsWebpages = [{
    title: 'Credibility and Professionalism',
    description: 'A well-designed webpage gives your business legitimacy and builds trust with potential customers.',
    icon: <CheckCircle2 className="text-sum-green" size={28} data-id="element-255" />
  }, {
    title: 'Visibility and Discoverability',
    description: 'It helps your business show up in search engines, making it easier for people to find you online.',
    icon: <Globe className="text-sum-green" size={28} data-id="element-256" />
  }, {
    title: 'Marketing and Sales',
    description: 'It serves as a hub for your marketing efforts, providing a place to convert visitors into customers.',
    icon: <Target className="text-sum-green" size={28} data-id="element-257" />
  }, {
    title: 'Information and Support',
    description: 'It gives customers 24/7 access to information about your products, services, and answers to common questions.',
    icon: <CheckCircle2 className="text-sum-green" size={28} data-id="element-258" />
  }, {
    title: 'Competitive Advantage',
    description: 'A strong online presence helps you stand out from competitors who may not be as digitally savvy.',
    icon: <Rocket className="text-sum-green" size={28} data-id="element-259" />
  }];
  const reasonsLandingPages = [{
    title: 'Conversion Focus',
    description: 'A landing page is designed specifically to drive a single action, like making a purchase or signing up, boosting ad performance.'
  }, {
    title: 'Tracking and Analytics',
    description: 'You can measure the effectiveness of your ads by tracking user behavior on your landing page.'
  }, {
    title: 'Message Alignment',
    description: "You can tailor the content on the page to match the ad's promise, ensuring a seamless experience for visitors."
  }, {
    title: 'Lead Generation',
    description: 'Landing pages help capture valuable leads through forms, which you can use for follow-up marketing.'
  }, {
    title: 'Lower Cost per Acquisition',
    description: 'A well-optimized landing page can increase conversion rates, reducing the cost of acquiring a customer through ads.'
  }];
  return <div className="min-h-screen bg-sum-light font-sans flex flex-col" data-id="element-260">
      <Navbar data-id="element-261" />

      <main className="flex-grow pt-20" data-id="element-262">
        {/* Hero Section */}
        <section className="relative bg-sum-charcoal text-white py-24 lg:py-32 overflow-hidden" data-id="element-263">
          <div className="absolute inset-0 z-0" data-id="element-264">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-sum-charcoal/90 z-10" data-id="element-265"></div>
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center opacity-20 mix-blend-overlay" data-id="element-266"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20" data-id="element-267">
            <Link to="/" className="inline-flex items-center text-gray-400 hover:text-sum-gold transition-colors mb-8 group" data-id="element-268">
              <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-269" />
              Back to Home
            </Link>

            <motion.div initial="hidden" animate="visible" variants={containerVariants} className="max-w-4xl" data-id="element-270">
              <motion.div variants={itemVariants} className="inline-block mb-4 px-4 py-1.5 rounded-full bg-sum-green/20 border border-sum-green/30 text-sum-green font-semibold tracking-wide text-sm" data-id="element-271">
                THE DIGITAL IMPERATIVE
              </motion.div>
              <motion.h1 variants={itemVariants} className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-6" data-id="element-272">
                Why Your Business <br className="hidden md:block" data-id="element-273" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sum-gold to-yellow-200" data-id="element-274">
                  Needs a Website
                </span>
              </motion.h1>
              <motion.p variants={itemVariants} className="text-xl md:text-2xl text-gray-300 max-w-2xl leading-relaxed" data-id="element-275">
                In a world that's constantly connected, your digital storefront
                is often the first impression you make. Make it count.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* Section 1: Top 5 Reasons for Webpages */}
        <section className="py-20 bg-white" data-id="element-276">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-277">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-16" data-id="element-278">
              <h2 className="text-3xl md:text-5xl font-extrabold text-sum-charcoal mb-4" data-id="element-279">
                Top 5 Reasons Why Webpages Are{' '}
                <span className="text-sum-green" data-id="element-280">Crucial</span>
              </h2>
              <div className="w-24 h-1.5 bg-sum-gold mx-auto rounded-full" data-id="element-281"></div>
            </motion.div>

            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-282">
              {reasonsWebpages.map((reason, index) => <motion.div key={index} variants={itemVariants} className={`bg-sum-light rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow relative overflow-hidden group ${index === 3 ? 'md:col-span-2 lg:col-span-1' : ''} ${index === 4 ? 'md:col-span-2 lg:col-span-2' : ''}`} data-id="element-283">
                  <div className="absolute -right-6 -top-6 text-9xl font-black text-gray-100 opacity-50 group-hover:scale-110 transition-transform duration-500 z-0" data-id="element-284">
                    {index + 1}
                  </div>
                  <div className="relative z-10" data-id="element-285">
                    <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center mb-6" data-id="element-286">
                      {reason.icon}
                    </div>
                    <h3 className="text-xl font-bold text-sum-charcoal mb-3" data-id="element-287">
                      {reason.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" data-id="element-288">
                      {reason.description}
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </section>

        {/* Section 2: Top 5 Reasons for Landing Pages */}
        <section className="py-20 bg-sum-light relative overflow-hidden" data-id="element-289">
          {/* Decorative background */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-sum-green/5 skew-x-12 translate-x-32 -z-10" data-id="element-290"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-291">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-16 max-w-3xl" data-id="element-292">
              <h2 className="text-3xl md:text-5xl font-extrabold text-sum-charcoal mb-4" data-id="element-293">
                Running Social Media Ads? <br data-id="element-294" />
                <span className="text-sum-gold" data-id="element-295">You Need a Landing Page.</span>
              </h2>
              <p className="text-lg text-gray-600" data-id="element-296">
                Sending ad traffic to your homepage is a common mistake. Here's
                why dedicated landing pages are the secret to ad success.
              </p>
            </motion.div>

            <div className="space-y-6 max-w-4xl mx-auto" data-id="element-297">
              {reasonsLandingPages.map((reason, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="flex bg-white rounded-2xl p-6 shadow-md border border-gray-100 hover:border-sum-gold/50 transition-colors" data-id="element-298">
                  <div className="flex-shrink-0 mr-6" data-id="element-299">
                    <div className="w-12 h-12 rounded-full bg-sum-charcoal text-sum-gold flex items-center justify-center font-bold text-xl" data-id="element-300">
                      {index + 1}
                    </div>
                  </div>
                  <div data-id="element-301">
                    <h3 className="text-xl font-bold text-sum-charcoal mb-2" data-id="element-302">
                      {reason.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-303">{reason.description}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 bg-sum-charcoal text-center px-4" data-id="element-304">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="max-w-3xl mx-auto" data-id="element-305">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-306">
              Ready to Build Your Digital Presence?
            </h2>
            <p className="text-xl text-gray-300 mb-10" data-id="element-307">
              Let's create a website or landing page that actually works for
              your business.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-308">
              <Link to="/#contact" className="px-8 py-4 bg-sum-green text-white font-bold rounded-md hover:bg-green-700 transition-colors text-lg" data-id="element-309">
                Get Started Today
              </Link>
              <Link to="/#services" className="px-8 py-4 bg-transparent border-2 border-gray-500 text-white font-bold rounded-md hover:border-white transition-colors text-lg" data-id="element-310">
                View Our Services
              </Link>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-311" />
    </div>;
}